import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import getSymbolFromCurrency from "currency-symbol-map";
import { GlobalContext } from "../../context/globalContext";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import {
  countExtraPages,
  countTotalExtraPages,
  INCLUDED_PAPER_PAGES,
  totalExtraPageChargePence,
} from "../../utils/extraPageFee";
import {
  AddOnRegistrantInput,
  createRegistrationDraft,
  deleteSavedRegistration,
  getMyRegistrations,
  getRegistrationCatalog,
  getSavedRegistration,
  MembershipTier,
  MyRegistration,
  PrimaryCategory,
  RegistrationCatalog,
  saveRegistrationProgress,
  SavedRegistrationFormData,
} from "../../api/registration";
import {
  DiscountCodePreview,
  estimateDiscountPence,
  validateDiscountCode,
} from "../../api/discountCodes";
import ConferenceAddOnForm from "./ConferenceAddOnForm";
import PricingWindowPanel from "./PricingWindowPanel";
import {
  firstFieldError,
  RegistrationFieldErrors,
  validateBanquetQuantity,
  validateStep1,
  validateStep2,
} from "./registrationFormValidation";
import {
  paperCountsFromEntries,
  papersToDraftInput,
  syncPaperCount,
  type RegistrationPaperEntry,
} from "../../utils/registrationPapers";

// Attendees who already have a confirmed registration for this event see a
// lightweight add-on form instead of the full registration flow. Scoped to
// DTPI 2026 only for now — matches the backend's ADD_ON_ELIGIBLE_EVENT_SLUGS
// in eventshouse-api/src/services/registration.service.ts.
const ADD_ON_ELIGIBLE_SLUG = "ieee-dtpi-2026";

const REGISTRATION_STEPS = [
  { num: 1, title: "Your details", hint: "Contact & membership information" },
  { num: 2, title: "Ticket selection", hint: "Conference package & add-ons" },
  { num: 3, title: "Review & pay", hint: "Confirm your registration" },
] as const;

const MEMBERSHIP_OPTIONS: { value: MembershipTier; label: string }[] = [
  {
    value: "ieee_student_life_retired",
    label: "IEEE Student / Life / Retired Member",
  },
  { value: "ieee_member", label: "IEEE Member" },
  { value: "non_ieee_member", label: "Non-IEEE Member" },
];

const PAPER_PLUS_MEMBERSHIP_OPTIONS = MEMBERSHIP_OPTIONS.filter(
  (opt) => opt.value !== "ieee_student_life_retired",
);

function pickFormatRate(
  rates: RegistrationCatalog["formatRates"],
  kind: "fullConference" | "addOn" | "singleDay",
  tier: MembershipTier,
  format: "in_person" | "virtual",
) {
  if (!rates) return null;
  return (
    rates[kind].find(
      (r) =>
        r.membershipTier === tier && r.participationFormat === format,
    ) ?? null
  );
}

const US_COUNTRY_NAMES = new Set([
  "United States of America (the)",
  "United States of America",
]);

function isIeeeTier(tier: MembershipTier): boolean {
  return tier !== "non_ieee_member";
}

function fieldClass(hasError: boolean): string {
  return `mt-1 w-full border p-2 ${
    hasError ? "border-red-500 bg-red-50" : "border-slate-300"
  }`;
}

function registrationOptionClass(selected: boolean): string {
  return [
    "block cursor-pointer rounded-sm p-4 text-sm transition-all",
    selected
      ? "border-2 border-amber-500 bg-amber-50 shadow-sm ring-1 ring-amber-400/40"
      : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
  ].join(" ");
}

function selectListenerRegistration(
  setPrimaryCategory: (value: PrimaryCategory) => void,
  setPapers: (value: RegistrationPaperEntry[]) => void,
  setMoreThanOnePaper: (value: boolean) => void,
  setAdditionalPaperCount: (value: number) => void,
  setFieldErrors: React.Dispatch<React.SetStateAction<RegistrationFieldErrors>>,
) {
  setPrimaryCategory("single_day");
  setPapers([{ paperId: "", paperTitle: "", paperCount: "" }]);
  setMoreThanOnePaper(false);
  setAdditionalPaperCount(0);
  setFieldErrors((prev) => ({
    ...prev,
    papers: undefined,
    paperFields: undefined,
    additionalPaperCount: undefined,
    primaryCategory: undefined,
  }));
}

const LOCKED_FIELD_CLASS =
  "mt-1 w-full border border-slate-200 bg-slate-50 p-2 text-slate-600";

function blankFormDefaults(user?: { firstName?: string; lastName?: string }) {
  return {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    companyName: "",
    country: "",
    countrySearch: "",
    countrySelectedFromList: false,
    state: "",
    membershipTier: "ieee_member" as MembershipTier,
    memberId: "",
    papers: [{ paperId: "", paperTitle: "", paperCount: "" }],
    moreThanOnePaper: false,
    additionalPaperCount: 0,
    participationFormat: "in_person" as "in_person" | "virtual",
    primaryCategory: "full_conference" as PrimaryCategory,
    banquetQuantity: 0,
  };
}

export default function ConferenceRegistrationForm() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isFreshStart = searchParams.get("fresh") === "1";
  const { currentUser, isAuthLoading } = useContext(GlobalContext);
  const skipNextSave = useRef(true);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState<RegistrationCatalog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<RegistrationFieldErrors>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [country, setCountry] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [countrySelectedFromList, setCountrySelectedFromList] = useState(false);
  const [state, setState] = useState("");
  const [membershipTier, setMembershipTier] =
    useState<MembershipTier>("ieee_member");
  const [memberId, setMemberId] = useState("");
  const [papers, setPapers] = useState<RegistrationPaperEntry[]>([
    { paperId: "", paperTitle: "", paperCount: "" },
  ]);
  const [moreThanOnePaper, setMoreThanOnePaper] = useState(false);
  const [additionalPaperCount, setAdditionalPaperCount] = useState(0);
  const [participationFormat, setParticipationFormat] = useState<
    "in_person" | "virtual"
  >("in_person");
  const [primaryCategory, setPrimaryCategory] =
    useState<PrimaryCategory>("full_conference");
  const [banquetQuantity, setBanquetQuantity] = useState(0);
  const [isLoadingSaved, setIsLoadingSaved] = useState(true);
  const [existingRegistration, setExistingRegistration] =
    useState<MyRegistration | null>(null);
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);

  const [discountCodeInput, setDiscountCodeInput] = useState("");
  const [appliedDiscountCode, setAppliedDiscountCode] = useState<string | null>(
    null,
  );
  const [appliedDiscountPreview, setAppliedDiscountPreview] =
    useState<DiscountCodePreview | null>(null);
  const [isCheckingDiscountCode, setIsCheckingDiscountCode] = useState(false);
  const [discountCodeError, setDiscountCodeError] = useState<string | null>(
    null,
  );

  const isPaperPlusAddon =
    catalog?.registrationPolicy === "paper_plus_addon";
  const membershipOptions = isPaperPlusAddon
    ? PAPER_PLUS_MEMBERSHIP_OPTIONS
    : MEMBERSHIP_OPTIONS;

  const signedInEmail = currentUser?.email ?? "";
  const signedInFirstName = currentUser?.firstName ?? "";
  const signedInLastName = currentUser?.lastName ?? "";
  const loginRedirect = slug
    ? `/login?redirect=${encodeURIComponent(`/events/${slug}/register`)}`
    : "/login";

  const buildFormSnapshot = useCallback(
    (): SavedRegistrationFormData => ({
      firstName: signedInFirstName,
      lastName: signedInLastName,
      companyName,
      country,
      countrySearch,
      countrySelectedFromList,
      state,
      membershipTier,
      memberId,
      papers: papers.map((paper) => ({
        paperId: paper.paperId,
        paperTitle: paper.paperTitle,
        paperCount: paper.paperCount
          ? parseInt(paper.paperCount, 10) || undefined
          : undefined,
        addOnRegistrants: paper.addOnRegistrants,
      })),
      moreThanOnePaper,
      additionalPaperCount,
      participationFormat,
      primaryCategory,
      banquetQuantity,
    }),
    [
      signedInFirstName,
      signedInLastName,
      companyName,
      country,
      countrySearch,
      countrySelectedFromList,
      state,
      membershipTier,
      memberId,
      papers,
      moreThanOnePaper,
      additionalPaperCount,
      participationFormat,
      primaryCategory,
      banquetQuantity,
    ],
  );

  const applyFormSnapshot = useCallback((data: SavedRegistrationFormData) => {
    if (data.companyName !== undefined) setCompanyName(data.companyName);
    if (data.country !== undefined) setCountry(data.country);
    if (data.countrySearch !== undefined) setCountrySearch(data.countrySearch);
    if (data.countrySelectedFromList !== undefined) {
      setCountrySelectedFromList(data.countrySelectedFromList);
    }
    if (data.state !== undefined) setState(data.state);
    if (data.membershipTier !== undefined) setMembershipTier(data.membershipTier);
    if (data.memberId !== undefined) setMemberId(data.memberId);
    if (data.papers !== undefined && data.papers.length > 0) {
      setPapers(
        data.papers.map((paper, index) => ({
          paperId: paper.paperId ?? "",
          paperTitle: paper.paperTitle ?? "",
          paperCount:
            paper.paperCount !== undefined && paper.paperCount !== null
              ? String(paper.paperCount)
              : "",
          // Legacy saved drafts kept a flat top-level addOnRegistrants list
          // for the (then-only) single paper — carry it over on restore.
          addOnRegistrants:
            paper.addOnRegistrants ??
            (index === 0 ? data.addOnRegistrants : undefined),
        })),
      );
    }
    if (data.moreThanOnePaper !== undefined) {
      setMoreThanOnePaper(data.moreThanOnePaper);
    }
    if (data.additionalPaperCount !== undefined) {
      setAdditionalPaperCount(data.additionalPaperCount);
    }
    if (data.participationFormat !== undefined) {
      setParticipationFormat(data.participationFormat);
    }
    if (data.primaryCategory !== undefined) {
      setPrimaryCategory(data.primaryCategory);
    }
    if (data.banquetQuantity !== undefined) {
      setBanquetQuantity(data.banquetQuantity);
    }
  }, []);

  const resetToBlankForm = useCallback(() => {
    const defaults = blankFormDefaults(currentUser ?? undefined);
    setCompanyName(defaults.companyName);
    setCountry(defaults.country);
    setCountrySearch(defaults.countrySearch);
    setCountrySelectedFromList(defaults.countrySelectedFromList);
    setState(defaults.state);
    setMembershipTier(defaults.membershipTier);
    setMemberId(defaults.memberId);
    setPapers(defaults.papers);
    setMoreThanOnePaper(defaults.moreThanOnePaper);
    setAdditionalPaperCount(defaults.additionalPaperCount);
    setParticipationFormat(defaults.participationFormat);
    setPrimaryCategory(defaults.primaryCategory);
    setBanquetQuantity(defaults.banquetQuantity);
    setStep(1);
    setFieldErrors({});
    setError(null);
  }, [currentUser]);

  useEffect(() => {
    if (!slug || !currentUser?.email || isFreshStart) {
      setIsLoadingSaved(false);
      return;
    }
    let cancelled = false;
    getSavedRegistration(slug)
      .then((saved) => {
        if (cancelled || !saved) return;
        applyFormSnapshot(saved.formData);
        if (saved.step >= 1 && saved.step <= 3) {
          setStep(saved.step);
        }
      })
      .catch(() => {
        /* no saved draft */
      })
      .finally(() => {
        if (!cancelled) {
          skipNextSave.current = true;
          setIsLoadingSaved(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, currentUser?.email, isFreshStart, applyFormSnapshot]);

  useEffect(() => {
    if (!slug || !currentUser?.email || isLoadingSaved) return;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    const hasContent =
      step > 1 ||
      signedInFirstName.trim().length > 0 ||
      signedInLastName.trim().length > 0 ||
      country.trim().length > 0 ||
      companyName.trim().length > 0;
    if (!hasContent) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveRegistrationProgress(slug, step, buildFormSnapshot()).catch(() => {
        /* silent — user can still complete form */
      });
    }, 700);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [
    slug,
    step,
    signedInFirstName,
    signedInLastName,
    country,
    companyName,
    currentUser?.email,
    isLoadingSaved,
    buildFormSnapshot,
  ]);

  async function handleClearForm() {
    if (!slug) return;
    resetToBlankForm();
    try {
      await deleteSavedRegistration(slug);
    } catch {
      /* form cleared locally */
    }
  }

  async function handleStartOver() {
    await handleClearForm();
  }

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setIsLoading(true);
    // Fetch immediately (token from localStorage if any). Do not wait for
    // isAuthLoading — that only blocked cold loads; auth still gates submit UI.
    getRegistrationCatalog(slug)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        if (!data.testTicket) {
          setPrimaryCategory((prev) =>
            prev === "test" ? "full_conference" : prev,
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            getUserFacingErrorMessage(
              err,
              "Failed to load registration.",
            ),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, currentUser?.id, currentUser?.role]);

  useEffect(() => {
    if (!slug || !currentUser?.email || slug !== ADD_ON_ELIGIBLE_SLUG) {
      setIsCheckingExisting(false);
      return;
    }
    let cancelled = false;
    getMyRegistrations()
      .then((registrations) => {
        if (cancelled) return;
        const match =
          registrations.find(
            (r) => r.event.slug === slug && r.status === "confirmed",
          ) ?? null;
        setExistingRegistration(match);
      })
      .catch(() => {
        /* best-effort — falls back to the normal registration form */
      })
      .finally(() => {
        if (!cancelled) setIsCheckingExisting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, currentUser?.email]);

  const filteredCountries = useMemo(() => {
    if (!catalog) return [];
    const q = countrySearch.trim().toLowerCase();
    if (!q) return catalog.countries.slice(0, 50);
    return catalog.countries
      .filter((c) => c.name.toLowerCase().includes(q))
      .slice(0, 50);
  }, [catalog, countrySearch]);

  const tierPricing = useMemo(() => {
    if (!catalog) return null;
    if (catalog.registrationPolicy === "paper_plus_addon") {
      const full = pickFormatRate(
        catalog.formatRates,
        "fullConference",
        membershipTier,
        participationFormat,
      );
      const single = pickFormatRate(
        catalog.formatRates,
        "singleDay",
        membershipTier,
        participationFormat,
      );
      return {
        fullPence: full?.pricePence ?? 0,
        singlePence: single?.pricePence ?? 0,
        fullId: full?.id ?? null,
        singleId: single?.id ?? null,
        currency: full?.currency ?? single?.currency ?? "usd",
      };
    }
    const full = catalog.fullConference.find(
      (r) => r.membershipTier === membershipTier,
    );
    const single = catalog.singleDay.find(
      (r) => r.membershipTier === membershipTier,
    );
    const currency =
      full?.currency ?? single?.currency ?? catalog.banquet?.currency ?? "usd";
    return {
      fullPence: full?.currentPricePence ?? 0,
      singlePence: single?.pricePence ?? 0,
      fullId: full?.id ?? null,
      singleId: single?.id ?? null,
      currency,
    };
  }, [catalog, membershipTier, participationFormat]);

  const totalPaperCount = isPaperPlusAddon
    ? papers.length
    : moreThanOnePaper
      ? 1 + additionalPaperCount
      : 1;

  useEffect(() => {
    // idass/icast paper count is driven by explicit "Add/Remove paper"
    // buttons below, not by a target count to sync toward.
    if (primaryCategory !== "full_conference" || isPaperPlusAddon) return;
    setPapers((prev) => syncPaperCount(prev, totalPaperCount));
  }, [primaryCategory, totalPaperCount, isPaperPlusAddon]);

  useEffect(() => {
    if (participationFormat !== "in_person" && moreThanOnePaper) {
      setMoreThanOnePaper(false);
      setAdditionalPaperCount(0);
      setPapers((prev) => syncPaperCount(prev, 1));
    }
  }, [participationFormat, moreThanOnePaper]);

  const priceSummary = useMemo(() => {
    if (!catalog) return null;
    if (primaryCategory === "test" && catalog.testTicket) {
      return {
        primaryPence: catalog.testTicket.pricePence,
        banquetPence: 0,
        extraPagePence: 0,
        additionalPaperPence: 0,
        additionalPaperCount: 0,
        addOnPence: 0,
        addOnCount: 0,
        extraPageCount: 0,
        total: catalog.testTicket.pricePence,
        currency: catalog.testTicket.currency,
        pricedComponents: [
          { ticketTypeId: catalog.testTicket.id, pence: catalog.testTicket.pricePence },
        ],
      };
    }
    if (!tierPricing) return null;

    if (catalog.registrationPolicy === "paper_plus_addon") {
      const primaryPence =
        primaryCategory === "full_conference"
          ? tierPricing.fullPence
          : tierPricing.singlePence;
      // Each extra paper is a full registration at the same rate as the
      // first — shown as "additional papers" like DTPI's fee model, except
      // priced at the full rate rather than a flat fee.
      const additionalPaperCountForPricing =
        primaryCategory === "full_conference"
          ? Math.max(0, papers.length - 1)
          : 0;
      const additionalPaperPence = additionalPaperCountForPricing * primaryPence;
      const primaryTicketTypeId =
        primaryCategory === "full_conference"
          ? tierPricing.fullId
          : tierPricing.singleId;
      const pricedComponents: { ticketTypeId: number | null; pence: number }[] =
        [{ ticketTypeId: primaryTicketTypeId, pence: primaryPence }];
      if (additionalPaperPence > 0) {
        // Extra papers here are more of the same primary ticket type, unlike
        // DTPI's flat additional-paper fee ticket.
        pricedComponents.push({
          ticketTypeId: primaryTicketTypeId,
          pence: additionalPaperPence,
        });
      }
      let addOnPence = 0;
      let addOnCount = 0;
      for (const paper of papers) {
        for (const person of paper.addOnRegistrants ?? []) {
          const rate = pickFormatRate(
            catalog.formatRates,
            "addOn",
            person.membershipTier ?? membershipTier,
            person.participationFormat ?? participationFormat,
          );
          addOnPence += rate?.pricePence ?? 0;
          addOnCount += 1;
          if (rate) {
            pricedComponents.push({ ticketTypeId: rate.id, pence: rate.pricePence });
          }
        }
      }
      return {
        primaryPence,
        banquetPence: 0,
        extraPagePence: 0,
        additionalPaperPence,
        additionalPaperCount: additionalPaperCountForPricing,
        addOnPence,
        addOnCount,
        extraPageCount: 0,
        total: primaryPence + additionalPaperPence + addOnPence,
        currency: tierPricing.currency,
        pricedComponents,
      };
    }

    const primaryPence =
      primaryCategory === "full_conference"
        ? tierPricing.fullPence
        : tierPricing.singlePence;
    const banquetPence =
      (catalog.banquet?.pricePence ?? 0) * banquetQuantity;
    const paperCounts = paperCountsFromEntries(papers);
    const extraPagePence =
      primaryCategory === "full_conference" &&
      paperCounts.length > 0 &&
      catalog.extraPageFee
        ? totalExtraPageChargePence(
            paperCounts,
            catalog.extraPageFee.pricePerPagePence,
          )
        : 0;
    const additionalPaperCountForPricing =
      primaryCategory === "full_conference" &&
      participationFormat === "in_person"
        ? Math.max(0, papers.length - 1)
        : 0;
    const additionalPaperPence =
      additionalPaperCountForPricing > 0
        ? (catalog.additionalPaperFee?.pricePence ?? 38000) *
          additionalPaperCountForPricing
        : 0;
    const primaryTicketTypeId =
      primaryCategory === "full_conference"
        ? tierPricing.fullId
        : tierPricing.singleId;
    const pricedComponents: { ticketTypeId: number | null; pence: number }[] = [
      { ticketTypeId: primaryTicketTypeId, pence: primaryPence },
    ];
    if (banquetPence > 0) {
      pricedComponents.push({
        ticketTypeId: catalog.banquet?.id ?? null,
        pence: banquetPence,
      });
    }
    if (extraPagePence > 0) {
      pricedComponents.push({
        ticketTypeId: catalog.extraPageFee?.id ?? null,
        pence: extraPagePence,
      });
    }
    if (additionalPaperPence > 0) {
      pricedComponents.push({
        ticketTypeId: catalog.additionalPaperFee?.id ?? null,
        pence: additionalPaperPence,
      });
    }
    return {
      primaryPence,
      banquetPence,
      extraPagePence,
      additionalPaperPence,
      additionalPaperCount: additionalPaperCountForPricing,
      addOnPence: 0,
      addOnCount: 0,
      extraPageCount:
        primaryCategory === "full_conference"
          ? countTotalExtraPages(paperCounts)
          : 0,
      total:
        primaryPence + banquetPence + extraPagePence + additionalPaperPence,
      currency: tierPricing.currency,
      pricedComponents,
    };
  }, [
    catalog,
    tierPricing,
    primaryCategory,
    banquetQuantity,
    papers,
    participationFormat,
    moreThanOnePaper,
    additionalPaperCount,
    membershipTier,
  ]);

  const estimatedDiscountPence = useMemo(
    () =>
      priceSummary
        ? estimateDiscountPence(priceSummary.pricedComponents, appliedDiscountPreview)
        : 0,
    [priceSummary, appliedDiscountPreview],
  );

  const showUsStates = US_COUNTRY_NAMES.has(country);

  function runStep1Validation(): RegistrationFieldErrors {
    return validateStep1({
      firstName: signedInFirstName,
      lastName: signedInLastName,
      companyName,
      country,
      state,
      membershipTier,
      memberId,
      countrySelectedFromList,
      isUsCountry: showUsStates,
    });
  }

  function handleNext() {
    const errors = runStep1Validation();
    setFieldErrors(errors);
    const firstErr = firstFieldError(errors);
    if (firstErr) {
      setError(firstErr);
      return;
    }
    setError(null);
    setStep(2);
    if (slug) {
      saveRegistrationProgress(slug, 2, buildFormSnapshot()).catch(() => {});
    }
  }

  function updatePaperAddOns(
    paperIndex: number,
    updater: (addOns: AddOnRegistrantInput[]) => AddOnRegistrantInput[],
  ) {
    setPapers((prev) =>
      prev.map((entry, i) =>
        i === paperIndex
          ? { ...entry, addOnRegistrants: updater(entry.addOnRegistrants ?? []) }
          : entry,
      ),
    );
  }

  function handleGoToReview() {
    const banquetErr = isPaperPlusAddon
      ? null
      : validateBanquetQuantity(banquetQuantity);
    const step2Errors = validateStep2({
      primaryCategory,
      papers,
      participationFormat,
      moreThanOnePaper,
      additionalPaperCount,
      expectedPaperCount: totalPaperCount,
      registrationPolicy: catalog?.registrationPolicy,
    });
    const errors: RegistrationFieldErrors = { ...step2Errors };
    if (banquetErr) {
      errors.banquetQuantity = banquetErr;
    }
    setFieldErrors(errors);
    const firstErr = firstFieldError(errors);
    if (firstErr) {
      setError(firstErr);
      return;
    }
    setError(null);
    setStep(3);
    if (slug) {
      saveRegistrationProgress(slug, 3, buildFormSnapshot()).catch(() => {});
    }
  }

  async function handleApplyDiscountCode() {
    if (!slug || !discountCodeInput.trim()) return;
    setIsCheckingDiscountCode(true);
    setDiscountCodeError(null);
    try {
      const preview = await validateDiscountCode(slug, discountCodeInput.trim());
      setAppliedDiscountCode(discountCodeInput.trim());
      setAppliedDiscountPreview(preview);
    } catch (err) {
      setAppliedDiscountCode(null);
      setAppliedDiscountPreview(null);
      setDiscountCodeError(
        getUserFacingErrorMessage(err, "This discount code is not valid."),
      );
    } finally {
      setIsCheckingDiscountCode(false);
    }
  }

  function handleRemoveDiscountCode() {
    setAppliedDiscountCode(null);
    setAppliedDiscountPreview(null);
    setDiscountCodeInput("");
    setDiscountCodeError(null);
  }

  async function handlePay() {
    if (!slug || !priceSummary || !signedInEmail) return;

    const errors = runStep1Validation();
    const step2Errors = validateStep2({
      primaryCategory,
      papers,
      participationFormat,
      moreThanOnePaper,
      additionalPaperCount,
      expectedPaperCount: totalPaperCount,
      registrationPolicy: catalog?.registrationPolicy,
    });
    Object.assign(errors, step2Errors);
    const banquetErr = isPaperPlusAddon
      ? null
      : validateBanquetQuantity(banquetQuantity);
    if (banquetErr) {
      errors.banquetQuantity = banquetErr;
    }
    setFieldErrors(errors);
    const firstErr = firstFieldError(errors);
    if (firstErr) {
      setError(firstErr);
      return;
    }

    const extraPagesRequested = priceSummary?.extraPageCount ?? 0;
    if (
      !isPaperPlusAddon &&
      extraPagesRequested > 0 &&
      !catalog?.extraPageFee
    ) {
      setError(
        "Extra page fees are not configured for this event yet. Please contact the organisers.",
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const origin = window.location.origin;
      const draftPapers =
        primaryCategory === "full_conference"
          ? isPaperPlusAddon
            ? papers.map((paper) => ({
                paperId: paper.paperId.trim(),
                paperTitle: paper.paperTitle.trim(),
                paperCount: 1,
                addOnRegistrants: paper.addOnRegistrants ?? [],
              }))
            : papersToDraftInput(papers)
          : undefined;
      const result = await createRegistrationDraft(slug, {
        firstName: signedInFirstName.trim(),
        lastName: signedInLastName.trim(),
        companyName: companyName.trim() || undefined,
        registrantState: state.trim() || undefined,
        registrantCountry: country,
        membershipTier,
        memberId: memberId.trim() || undefined,
        papers: draftPapers,
        participationFormat,
        primaryCategory,
        banquetQuantity: isPaperPlusAddon ? 0 : banquetQuantity,
        discountCode: appliedDiscountCode ?? undefined,
        successUrl: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/checkout/cancelled`,
      });

      sessionStorage.setItem("pendingRegistrationId", result.registrationId);
      sessionStorage.setItem("pendingRegistrationEventSlug", slug);
      sessionStorage.setItem("pendingCheckoutType", "registration");
      window.location.href = result.checkoutUrl;
    } catch (err) {
      setError(
        getUserFacingErrorMessage(err, "Unable to start checkout."),
      );
      setIsSubmitting(false);
    }
  }

  if (!slug) {
    return <p className="text-center py-20">Invalid event.</p>;
  }

  if (isAuthLoading || isLoading || isLoadingSaved || isCheckingExisting) {
    return (
      <LoadingPanel
        title={isAuthLoading ? "Verifying your account" : "Preparing registration"}
        subtitle={
          isAuthLoading
            ? "Confirming your signed-in session."
            : "Loading rates and registration options."
        }
      />
    );
  }

  if (!currentUser?.email) {
    return (
      <div className="max-w-[500px] mx-auto py-20 text-center px-6">
        <h1 className="text-2xl font-bold mb-3">Sign in required</h1>
        <p className="text-slate-600 mb-6">
          Please sign in to register. Your account email will be used for this
          registration and checkout.
        </p>
        <NavLink
          to={loginRedirect}
          className="inline-block bg-slate-800 text-white px-8 py-3 hover:bg-slate-700"
        >
          Sign in to continue
        </NavLink>
        <p className="mt-4 text-sm text-slate-500">
          Don&apos;t have an account?{" "}
          <NavLink to="/signup" className="underline">
            Create one
          </NavLink>
        </p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="max-w-[800px] mx-auto py-20 text-center">
        <ErrorAlert className="mb-4">
          {error ?? "Registration unavailable."}
        </ErrorAlert>
        <NavLink to={`/${slug}`} className="underline">
          Back to event
        </NavLink>
      </div>
    );
  }

  if (existingRegistration) {
    return (
      <ConferenceAddOnForm
        registration={existingRegistration}
        catalog={catalog}
        eventSlug={slug}
      />
    );
  }

  const currency = priceSummary?.currency.toUpperCase() ?? "USD";
  const symbol = getSymbolFromCurrency(currency) ?? "$";

  return (
    <>
      <Helmet>
        <title>Register | {catalog.event.title}</title>
      </Helmet>

      {isSubmitting && (
        <div className="fixed inset-0 z-50 bg-white/90 flex items-center justify-center">
          <LoadingPanel
            title="Preparing secure checkout"
            subtitle="Redirecting you to Stripe. Please do not close this window."
            className="py-12"
          />
        </div>
      )}

      <div className="max-w-[800px] mx-auto py-8 mb-32 bg-white border border-slate-200 px-6 sm:px-8">
        <NavLink
          to={`/${slug}`}
          className="text-sm text-slate-500 underline mb-4 inline-block"
        >
          ← Back to event
        </NavLink>
        <h1 className="text-2xl font-bold mb-2">Conference Registration</h1>
        <p className="text-slate-600 mb-4">{catalog.event.title}</p>

        {catalog.testPricing?.enabled && catalog.testPricing.label && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-sm text-amber-900 rounded-sm">
            {catalog.testPricing.label}
          </div>
        )}

        {catalog.pricingWindowOptions && (
          <PricingWindowPanel options={catalog.pricingWindowOptions} compact />
        )}

        <ol className="flex flex-col sm:flex-row gap-3 sm:gap-0 mb-8 mt-6 text-sm">
          {REGISTRATION_STEPS.map((s, idx) => (
            <li
              key={s.num}
              className={`flex-1 flex items-start gap-2 sm:px-3 ${
                idx > 0 ? "sm:border-l sm:border-slate-200" : ""
              }`}
            >
              <span
                className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${
                  step === s.num
                    ? "bg-slate-800 text-white"
                    : step > s.num
                      ? "bg-slate-200 text-slate-600"
                      : "border border-slate-300 text-slate-400"
                }`}
              >
                {s.num}
              </span>
              <div>
                <p
                  className={`font-semibold leading-tight ${
                    step === s.num ? "text-slate-900" : "text-slate-500"
                  }`}
                >
                  {s.title}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 hidden sm:block">
                  {s.hint}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-semibold text-lg">Registrant details</h2>
            <label className="block">
              <span className="text-sm font-medium">Email</span>
              <input
                type="email"
                className="mt-1 w-full border border-slate-200 bg-slate-50 p-2 text-slate-600"
                value={signedInEmail}
                readOnly
                disabled
                aria-readonly="true"
              />
              <span className="text-xs text-slate-500">
                Locked to your signed-in account
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium">First name *</span>
                <input
                  className={LOCKED_FIELD_CLASS}
                  value={signedInFirstName}
                  readOnly
                  disabled
                  aria-readonly="true"
                  autoComplete="given-name"
                />
                {fieldErrors.firstName && (
                  <span className="text-xs text-red-600">{fieldErrors.firstName}</span>
                )}
              </label>
              <label className="block">
                <span className="text-sm font-medium">Last name *</span>
                <input
                  className={LOCKED_FIELD_CLASS}
                  value={signedInLastName}
                  readOnly
                  disabled
                  aria-readonly="true"
                  autoComplete="family-name"
                />
                {fieldErrors.lastName && (
                  <span className="text-xs text-red-600">{fieldErrors.lastName}</span>
                )}
              </label>
            </div>
            <p className="text-xs text-slate-500 -mt-2">
              Name locked to your signed-in account
            </p>
            <label className="block">
              <span className="text-sm font-medium">Company / affiliation</span>
              <input
                className={fieldClass(Boolean(fieldErrors.companyName))}
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setFieldErrors((prev) => ({ ...prev, companyName: undefined }));
                }}
                maxLength={200}
                autoComplete="organization"
              />
              {fieldErrors.companyName && (
                <span className="text-xs text-red-600">{fieldErrors.companyName}</span>
              )}
            </label>
            <label className="block">
              <span className="text-sm font-medium">Country *</span>
              <input
                className={fieldClass(Boolean(fieldErrors.country))}
                placeholder="Search countries..."
                value={countrySearch || country}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setCountry("");
                  setCountrySelectedFromList(false);
                  setFieldErrors((prev) => ({ ...prev, country: undefined }));
                }}
                autoComplete="country-name"
              />
              {countrySearch && !country && filteredCountries.length > 0 && (
                <ul className="border border-slate-200 max-h-40 overflow-y-auto bg-white">
                  {filteredCountries.map((c) => (
                    <li key={c.code}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm"
                        onClick={() => {
                          setCountry(c.name);
                          setCountrySearch(c.name);
                          setCountrySelectedFromList(true);
                          setState("");
                          setFieldErrors((prev) => ({ ...prev, country: undefined }));
                        }}
                      >
                        {c.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {fieldErrors.country && (
                <span className="text-xs text-red-600">{fieldErrors.country}</span>
              )}
            </label>
            {showUsStates && (
              <label className="block">
                <span className="text-sm font-medium">State / province *</span>
                <select
                  className={fieldClass(Boolean(fieldErrors.state))}
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, state: undefined }));
                  }}
                >
                  <option value="">Select state</option>
                  {catalog.usStates.map((s) => (
                    <option key={s.code} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.state && (
                  <span className="text-xs text-red-600">{fieldErrors.state}</span>
                )}
              </label>
            )}
            {!showUsStates && country && (
              <label className="block">
                <span className="text-sm font-medium">State / province</span>
                <input
                  className={fieldClass(Boolean(fieldErrors.state))}
                  value={state}
                  onChange={(e) => {
                    setState(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, state: undefined }));
                  }}
                  maxLength={100}
                  autoComplete="address-level1"
                />
                {fieldErrors.state && (
                  <span className="text-xs text-red-600">{fieldErrors.state}</span>
                )}
              </label>
            )}
            <label className="block">
              <span className="text-sm font-medium">IEEE membership tier *</span>
              <select
                className="mt-1 w-full border border-slate-300 p-2"
                value={membershipTier}
                onChange={(e) =>
                  setMembershipTier(e.target.value as MembershipTier)
                }
              >
                {membershipOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            {isIeeeTier(membershipTier) && (
              <label className="block">
                <span className="text-sm font-medium">IEEE Member ID *</span>
                <input
                  className={fieldClass(Boolean(fieldErrors.memberId))}
                  value={memberId}
                  onChange={(e) => {
                    setMemberId(e.target.value.replace(/\s/g, ""));
                    setFieldErrors((prev) => ({ ...prev, memberId: undefined }));
                  }}
                  maxLength={50}
                  inputMode="text"
                  autoComplete="off"
                />
                {fieldErrors.memberId && (
                  <span className="text-xs text-red-600">{fieldErrors.memberId}</span>
                )}
              </label>
            )}
            <label className="block">
              <span className="text-sm font-medium">Participation format *</span>
              <select
                className="mt-1 w-full border border-slate-300 p-2"
                value={participationFormat}
                onChange={(e) =>
                  setParticipationFormat(
                    e.target.value as "in_person" | "virtual",
                  )
                }
              >
                <option value="in_person">
                  {isPaperPlusAddon ? "Onsite" : "In-person"}
                </option>
                <option value="virtual">Virtual</option>
              </select>
              {isPaperPlusAddon && (
                <p className="text-xs text-slate-500 mt-1">
                  Attendance format changes the registration rate.
                </p>
              )}
            </label>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button
                type="button"
                className="eh-btn-primary px-6 py-2"
                onClick={handleNext}
              >
                Continue
              </button>
              <button
                type="button"
                className="eh-btn-secondary px-6 py-2"
                onClick={handleClearForm}
              >
                Clear form
              </button>
            </div>
          </div>
        )}

        {step === 2 && priceSummary && tierPricing && (
          <div className="space-y-4">
            <h2 className="font-semibold text-lg">Select registration item</h2>

            <fieldset className="space-y-3">
              <legend className="font-semibold text-base text-slate-900 mb-1">
                Are you registering a paper?
              </legend>
              <div className="grid sm:grid-cols-2 gap-3">
                <label
                  className={registrationOptionClass(
                    primaryCategory === "full_conference",
                  )}
                >
                  <input
                    type="radio"
                    name="primary"
                    className="sr-only"
                    checked={primaryCategory === "full_conference"}
                    onChange={() => setPrimaryCategory("full_conference")}
                  />
                  <p
                    className={`font-semibold ${
                      primaryCategory === "full_conference"
                        ? "text-amber-950"
                        : "text-slate-800"
                    }`}
                  >
                    Yes — I am registering a paper
                  </p>
                  <p className="mt-2 text-slate-700">
                    Enter your <strong>Paper ID</strong> and{" "}
                    <strong>Paper title</strong> below.
                    {isPaperPlusAddon && (
                      <>
                        {" "}
                        Each full registration covers <strong>one paper</strong>.
                      </>
                    )}
                  </p>
                  <p
                    className={`mt-3 font-semibold tabular-nums ${
                      primaryCategory === "full_conference"
                        ? "text-amber-950"
                        : "text-slate-800"
                    }`}
                  >
                    {isPaperPlusAddon ? "Full Registration" : "Full Conference"}{" "}
                    — {symbol}
                    {(tierPricing.fullPence / 100).toFixed(2)}
                    {catalog.activePricingWindowLabel && !isPaperPlusAddon && (
                      <span className="block text-xs font-normal text-amber-800 mt-0.5">
                        {catalog.activePricingWindowLabel} rate
                      </span>
                    )}
                    {isPaperPlusAddon && (
                      <span className="block text-xs font-normal text-amber-800 mt-0.5">
                        {participationFormat === "virtual"
                          ? "Virtual"
                          : "Onsite"}{" "}
                        rate
                      </span>
                    )}
                  </p>
                </label>

                {(!isPaperPlusAddon || catalog.singleDay.length > 0) && (
                  <label
                    className={registrationOptionClass(
                      primaryCategory === "single_day",
                    )}
                  >
                    <input
                      type="radio"
                      name="primary"
                      className="sr-only"
                      checked={primaryCategory === "single_day"}
                      onChange={() =>
                        selectListenerRegistration(
                          setPrimaryCategory,
                          setPapers,
                          setMoreThanOnePaper,
                          setAdditionalPaperCount,
                          setFieldErrors,
                        )
                      }
                    />
                    <p
                      className={`font-semibold ${
                        primaryCategory === "single_day"
                          ? "text-slate-900"
                          : "text-slate-800"
                      }`}
                    >
                      No — I am not registering a paper
                    </p>
                    <p
                      className={`mt-3 font-semibold tabular-nums ${
                        primaryCategory === "single_day"
                          ? "text-slate-900"
                          : "text-slate-800"
                      }`}
                    >
                      Listener — {symbol}
                      {(tierPricing.singlePence / 100).toFixed(2)}
                      {isPaperPlusAddon && (
                        <span className="block text-xs font-normal text-slate-600 mt-0.5">
                          {participationFormat === "virtual"
                            ? "Virtual"
                            : "Onsite"}{" "}
                          rate
                        </span>
                      )}
                    </p>
                  </label>
                )}
              </div>

              {catalog.testTicket && (
                <label className="flex items-center gap-2 text-amber-900 rounded-sm border border-amber-200 bg-amber-50/40 p-3">
                  <input
                    type="radio"
                    name="primary"
                    checked={primaryCategory === "test"}
                    onChange={() => {
                      setPrimaryCategory("test");
                      setBanquetQuantity(0);
                      setPapers([{ paperId: "", paperTitle: "", paperCount: "" }]);
                      setMoreThanOnePaper(false);
                      setAdditionalPaperCount(0);
                    }}
                  />
                  {catalog.testTicket.name} — {symbol}
                  {(catalog.testTicket.pricePence / 100).toFixed(2)} (Stripe
                  test checkout)
                </label>
              )}
            </fieldset>

            {primaryCategory === "full_conference" && (
              <div className="space-y-4">
                {papers.map((paper, index) => {
                  const addOns = paper.addOnRegistrants ?? [];
                  return (
                  <div key={`paper-${index}`} className="space-y-4">
                    <div
                      className={`grid gap-4 border border-slate-200 rounded-sm p-4 bg-white ${
                        isPaperPlusAddon ? "sm:grid-cols-2" : "sm:grid-cols-3"
                      }`}
                    >
                      <div
                        className={`${
                          isPaperPlusAddon ? "sm:col-span-2" : "sm:col-span-3"
                        } flex items-center justify-between`}
                      >
                        <p className="text-sm font-semibold text-slate-800">
                          Paper {index + 1}
                        </p>
                        {isPaperPlusAddon && papers.length > 1 && (
                          <button
                            type="button"
                            className="text-xs text-red-600 underline"
                            onClick={() =>
                              setPapers((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                          >
                            Remove this paper
                          </button>
                        )}
                      </div>
                      <label className="block">
                        <span className="text-sm font-medium">Paper ID *</span>
                        <input
                          className={fieldClass(
                            Boolean(fieldErrors.paperFields?.[index]?.paperId),
                          )}
                          value={paper.paperId}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\s/g, "");
                            setPapers((prev) =>
                              prev.map((entry, i) =>
                                i === index ? { ...entry, paperId: value } : entry,
                              ),
                            );
                            setFieldErrors((prev) => ({
                              ...prev,
                              papers: undefined,
                              paperFields: undefined,
                            }));
                          }}
                          maxLength={100}
                          inputMode="text"
                          autoComplete="off"
                          placeholder="Enter your Paper ID"
                          required
                        />
                        {fieldErrors.paperFields?.[index]?.paperId && (
                          <span className="text-xs text-red-600">
                            {fieldErrors.paperFields[index]?.paperId}
                          </span>
                        )}
                      </label>
                      <label className="block">
                        <span className="text-sm font-medium">Paper title *</span>
                        <input
                          className={fieldClass(
                            Boolean(fieldErrors.paperFields?.[index]?.paperTitle),
                          )}
                          value={paper.paperTitle}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPapers((prev) =>
                              prev.map((entry, i) =>
                                i === index ? { ...entry, paperTitle: value } : entry,
                              ),
                            );
                            setFieldErrors((prev) => ({
                              ...prev,
                              papers: undefined,
                              paperFields: undefined,
                            }));
                          }}
                          maxLength={500}
                          autoComplete="off"
                          placeholder="Enter your paper title"
                          required
                        />
                        {fieldErrors.paperFields?.[index]?.paperTitle && (
                          <span className="text-xs text-red-600">
                            {fieldErrors.paperFields[index]?.paperTitle}
                          </span>
                        )}
                      </label>
                      {!isPaperPlusAddon && (
                        <label className="block">
                          <span className="text-sm font-medium">
                            Paper page count *
                          </span>
                          <input
                            type="number"
                            min={1}
                            max={500}
                            className={fieldClass(
                              Boolean(
                                fieldErrors.paperFields?.[index]?.paperCount,
                              ),
                            )}
                            value={paper.paperCount}
                            onChange={(e) => {
                              const value = e.target.value;
                              setPapers((prev) =>
                                prev.map((entry, i) =>
                                  i === index
                                    ? { ...entry, paperCount: value }
                                    : entry,
                                ),
                              );
                              setFieldErrors((prev) => ({
                                ...prev,
                                papers: undefined,
                                paperFields: undefined,
                              }));
                            }}
                            placeholder="Number of pages"
                            required
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            First{" "}
                            {catalog.extraPageFee?.includedPages ??
                              INCLUDED_PAPER_PAGES}{" "}
                            pages included.
                            {catalog.extraPageFee ? (
                              <>
                                {symbol}
                                {(
                                  catalog.extraPageFee.pricePerPagePence / 100
                                ).toFixed(0)}{" "}
                                per additional page.
                              </>
                            ) : (
                              " Extra page fees are not configured yet."
                            )}
                          </p>
                          {fieldErrors.paperFields?.[index]?.paperCount && (
                            <span className="text-xs text-red-600">
                              {fieldErrors.paperFields[index]?.paperCount}
                            </span>
                          )}
                          {paper.paperCount &&
                            !fieldErrors.paperFields?.[index]?.paperCount &&
                            countExtraPages(parseInt(paper.paperCount, 10)) > 0 &&
                            catalog.extraPageFee && (
                              <p className="text-xs text-amber-800 mt-1">
                                Extra pages for this paper (
                                {countExtraPages(parseInt(paper.paperCount, 10))}
                                ): {symbol}
                                {(
                                  countExtraPages(parseInt(paper.paperCount, 10)) *
                                  (catalog.extraPageFee.pricePerPagePence / 100)
                                ).toFixed(2)}
                              </p>
                            )}
                        </label>
                      )}
                    </div>

                    {isPaperPlusAddon && (
                      <div className="space-y-3 border border-slate-200 rounded-sm p-4 bg-slate-50">
                        <p className="text-sm font-semibold text-slate-800">
                          Additional people under this paper
                        </p>
                        <p className="text-xs text-slate-600">
                          If more than one person registers under this paper,
                          each additional person needs an add-on ticket. Provide
                          name, email, and IEEE Member ID when applicable. Rate
                          follows their membership and attendance format.
                        </p>
                        {addOns.map((person, personIndex) => {
                          const personTier =
                            person.membershipTier ?? membershipTier;
                          const needsMemberId = isIeeeTier(personTier);
                          return (
                          <div
                            key={`addon-${index}-${personIndex}`}
                            className="grid sm:grid-cols-2 gap-3 border border-slate-200 rounded-sm p-3 bg-white"
                          >
                            <label className="block">
                              <span className="text-sm font-medium">Name *</span>
                              <input
                                className={fieldClass(
                                  Boolean(
                                    fieldErrors.addOnFields?.[index]?.[personIndex]
                                      ?.name,
                                  ),
                                )}
                                value={person.name}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updatePaperAddOns(index, (rows) =>
                                    rows.map((row, i) =>
                                      i === personIndex
                                        ? { ...row, name: value }
                                        : row,
                                    ),
                                  );
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    addOnRegistrants: undefined,
                                    addOnFields: undefined,
                                  }));
                                }}
                                maxLength={100}
                              />
                              {fieldErrors.addOnFields?.[index]?.[personIndex]
                                ?.name && (
                                <span className="text-xs text-red-600">
                                  {
                                    fieldErrors.addOnFields[index]?.[personIndex]
                                      ?.name
                                  }
                                </span>
                              )}
                            </label>
                            <label className="block">
                              <span className="text-sm font-medium">Email *</span>
                              <input
                                type="email"
                                className={fieldClass(
                                  Boolean(
                                    fieldErrors.addOnFields?.[index]?.[personIndex]
                                      ?.email,
                                  ),
                                )}
                                value={person.email}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  updatePaperAddOns(index, (rows) =>
                                    rows.map((row, i) =>
                                      i === personIndex
                                        ? { ...row, email: value }
                                        : row,
                                    ),
                                  );
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    addOnRegistrants: undefined,
                                    addOnFields: undefined,
                                  }));
                                }}
                                maxLength={254}
                              />
                              {fieldErrors.addOnFields?.[index]?.[personIndex]
                                ?.email && (
                                <span className="text-xs text-red-600">
                                  {
                                    fieldErrors.addOnFields[index]?.[personIndex]
                                      ?.email
                                  }
                                </span>
                              )}
                            </label>
                            <label className="block">
                              <span className="text-sm font-medium">
                                Membership *
                              </span>
                              <select
                                className="mt-1 w-full border border-slate-300 p-2 text-sm"
                                value={personTier}
                                onChange={(e) => {
                                  const value = e.target.value as MembershipTier;
                                  updatePaperAddOns(index, (rows) =>
                                    rows.map((row, i) =>
                                      i === personIndex
                                        ? {
                                            ...row,
                                            membershipTier: value,
                                            memberId:
                                              value === "non_ieee_member"
                                                ? ""
                                                : row.memberId,
                                          }
                                        : row,
                                    ),
                                  );
                                  setFieldErrors((prev) => ({
                                    ...prev,
                                    addOnRegistrants: undefined,
                                    addOnFields: undefined,
                                  }));
                                }}
                              >
                                {membershipOptions.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="block">
                              <span className="text-sm font-medium">Format *</span>
                              <select
                                className="mt-1 w-full border border-slate-300 p-2 text-sm"
                                value={
                                  person.participationFormat ?? participationFormat
                                }
                                onChange={(e) => {
                                  const value = e.target.value as
                                    | "in_person"
                                    | "virtual";
                                  updatePaperAddOns(index, (rows) =>
                                    rows.map((row, i) =>
                                      i === personIndex
                                        ? { ...row, participationFormat: value }
                                        : row,
                                    ),
                                  );
                                }}
                              >
                                <option value="in_person">Onsite</option>
                                <option value="virtual">Virtual</option>
                              </select>
                            </label>
                            {needsMemberId && (
                              <label className="block sm:col-span-2">
                                <span className="text-sm font-medium">
                                  IEEE Member ID *
                                </span>
                                <input
                                  className={fieldClass(
                                    Boolean(
                                      fieldErrors.addOnFields?.[index]?.[
                                        personIndex
                                      ]?.memberId,
                                    ),
                                  )}
                                  value={person.memberId ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\s/g,
                                      "",
                                    );
                                    updatePaperAddOns(index, (rows) =>
                                      rows.map((row, i) =>
                                        i === personIndex
                                          ? { ...row, memberId: value }
                                          : row,
                                      ),
                                    );
                                    setFieldErrors((prev) => ({
                                      ...prev,
                                      addOnRegistrants: undefined,
                                      addOnFields: undefined,
                                    }));
                                  }}
                                  maxLength={50}
                                  autoComplete="off"
                                />
                                {fieldErrors.addOnFields?.[index]?.[personIndex]
                                  ?.memberId && (
                                  <span className="text-xs text-red-600">
                                    {
                                      fieldErrors.addOnFields[index]?.[
                                        personIndex
                                      ]?.memberId
                                    }
                                  </span>
                                )}
                              </label>
                            )}
                            <div className="sm:col-span-2">
                              <button
                                type="button"
                                className="text-xs text-red-600 underline"
                                onClick={() =>
                                  updatePaperAddOns(index, (rows) =>
                                    rows.filter((_, i) => i !== personIndex),
                                  )
                                }
                              >
                                Remove person
                              </button>
                            </div>
                          </div>
                          );
                        })}
                        {addOns.length < 10 && (
                          <button
                            type="button"
                            className="eh-btn-secondary px-3 py-1.5 text-sm"
                            onClick={() =>
                              updatePaperAddOns(index, (rows) => [
                                ...rows,
                                {
                                  name: "",
                                  email: "",
                                  membershipTier,
                                  participationFormat,
                                  memberId: "",
                                },
                              ])
                            }
                          >
                            Add person
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}

                {isPaperPlusAddon && (
                  <>
                    {papers.length < 11 && (
                      <button
                        type="button"
                        className="eh-btn-secondary px-3 py-1.5 text-sm"
                        onClick={() =>
                          setPapers((prev) => [
                            ...prev,
                            {
                              paperId: "",
                              paperTitle: "",
                              paperCount: "1",
                              addOnRegistrants: [],
                            },
                          ])
                        }
                      >
                        Add another paper
                      </button>
                    )}
                    {fieldErrors.addOnRegistrants && (
                      <p className="text-xs text-red-600">
                        {fieldErrors.addOnRegistrants}
                      </p>
                    )}
                  </>
                )}

                {!isPaperPlusAddon && participationFormat === "in_person" && (
                  <div className="space-y-3 border border-slate-200 rounded-sm p-4 bg-slate-50">
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={moreThanOnePaper}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setMoreThanOnePaper(checked);
                          if (!checked) {
                            setAdditionalPaperCount(0);
                            setPapers((prev) => syncPaperCount(prev, 1));
                          } else {
                            setAdditionalPaperCount(1);
                            setPapers((prev) => syncPaperCount(prev, 2));
                          }
                          setFieldErrors((prev) => ({
                            ...prev,
                            additionalPaperCount: undefined,
                            papers: undefined,
                            paperFields: undefined,
                          }));
                        }}
                      />
                      <span className="text-sm text-slate-800">
                        I am registering more than one paper
                      </span>
                    </label>
                    <p className="text-xs text-slate-600">
                      {catalog.additionalPaperFee?.description ??
                        "The same author who has paid in-person full registration fees can register one additional paper for an extra of $380 USD."}
                    </p>
                    {moreThanOnePaper && (
                      <label className="block max-w-xs">
                        <span className="text-sm font-medium">
                          How many additional papers? *
                        </span>
                        <select
                          className={fieldClass(
                            Boolean(fieldErrors.additionalPaperCount),
                          )}
                          value={additionalPaperCount || ""}
                          onChange={(e) => {
                            const count = parseInt(e.target.value, 10) || 0;
                            setAdditionalPaperCount(count);
                            setPapers((prev) => syncPaperCount(prev, 1 + count));
                            setFieldErrors((prev) => ({
                              ...prev,
                              additionalPaperCount: undefined,
                              papers: undefined,
                              paperFields: undefined,
                            }));
                          }}
                        >
                          <option value="">Select number</option>
                          {Array.from({ length: 10 }, (_, i) => i + 1).map(
                            (count) => (
                              <option key={count} value={count}>
                                {count}
                              </option>
                            ),
                          )}
                        </select>
                        {fieldErrors.additionalPaperCount && (
                          <span className="text-xs text-red-600">
                            {fieldErrors.additionalPaperCount}
                          </span>
                        )}
                      </label>
                    )}
                  </div>
                )}

              </div>
            )}

            {!isPaperPlusAddon && (
              <p className="text-sm text-slate-600 border border-slate-200 bg-slate-50 p-3">
                Full Conference and Listener registrations do not include the
                banquet dinner. Banquet tickets must be purchased separately.
              </p>
            )}

            {!isPaperPlusAddon &&
              catalog.banquet &&
              primaryCategory !== "test" && (
              <label className="block">
                <span className="text-sm font-medium">
                  Banquet Tickets ({symbol}
                  {(catalog.banquet.pricePence / 100).toFixed(0)} each)
                </span>
                <input
                  type="number"
                  min={0}
                  max={20}
                  className={fieldClass(Boolean(fieldErrors.banquetQuantity))}
                  value={banquetQuantity}
                  onChange={(e) => {
                    setBanquetQuantity(
                      Math.max(0, parseInt(e.target.value, 10) || 0),
                    );
                    setFieldErrors((prev) => ({
                      ...prev,
                      banquetQuantity: undefined,
                    }));
                  }}
                />
                {fieldErrors.banquetQuantity && (
                  <span className="text-xs text-red-600">
                    {fieldErrors.banquetQuantity}
                  </span>
                )}
              </label>
            )}

            <div className="border border-slate-200 p-4 bg-slate-50 text-sm">
              <p className="font-semibold mb-2">Price summary</p>
              <p>
                Primary registration: {symbol}
                {(priceSummary.primaryPence / 100).toFixed(2)}
              </p>
              {priceSummary.extraPageCount > 0 && (
                <p>
                  Extra pages ({priceSummary.extraPageCount}): {symbol}
                  {(priceSummary.extraPagePence / 100).toFixed(2)}
                </p>
              )}
              {priceSummary.additionalPaperCount > 0 && (
                <p>
                  Additional papers ({priceSummary.additionalPaperCount}):{" "}
                  {symbol}
                  {(priceSummary.additionalPaperPence / 100).toFixed(2)}
                </p>
              )}
              {banquetQuantity > 0 && (
                <p>
                  Banquet tickets ({banquetQuantity}): {symbol}
                  {(priceSummary.banquetPence / 100).toFixed(2)}
                </p>
              )}
              {(priceSummary.addOnCount ?? 0) > 0 && (
                <p>
                  Additional people ({priceSummary.addOnCount}): {symbol}
                  {((priceSummary.addOnPence ?? 0) / 100).toFixed(2)}
                </p>
              )}
              {estimatedDiscountPence > 0 && (
                <p className="text-green-700">
                  Discount ({appliedDiscountCode}): -{symbol}
                  {(estimatedDiscountPence / 100).toFixed(2)}
                </p>
              )}
              <p className="font-bold mt-2">
                Total: {symbol}
                {((priceSummary.total - estimatedDiscountPence) / 100).toFixed(2)}{" "}
                {currency}
              </p>
            </div>

            <div className="border border-slate-200 p-4 text-sm">
              <p className="font-semibold mb-2">Discount code</p>
              {appliedDiscountCode ? (
                <div className="flex items-center gap-3">
                  <span className="text-green-800">
                    Code <span className="font-mono">{appliedDiscountCode}</span>{" "}
                    applied
                    {estimatedDiscountPence > 0 ? (
                      <>
                        {" "}
                        — {symbol}
                        {(estimatedDiscountPence / 100).toFixed(2)} off.
                      </>
                    ) : (
                      " — it doesn't apply to your current selection."
                    )}
                  </span>
                  <button
                    type="button"
                    className="text-slate-600 underline text-xs"
                    onClick={handleRemoveDiscountCode}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    className="border p-2 text-sm uppercase"
                    placeholder="Enter code"
                    value={discountCodeInput}
                    onChange={(e) => setDiscountCodeInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="eh-btn-secondary text-xs px-3 py-2"
                    disabled={isCheckingDiscountCode || !discountCodeInput.trim()}
                    onClick={handleApplyDiscountCode}
                  >
                    {isCheckingDiscountCode ? "Checking…" : "Apply"}
                  </button>
                </div>
              )}
              {discountCodeError && (
                <ErrorAlert className="mt-2" inline>
                  {discountCodeError}
                </ErrorAlert>
              )}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                type="button"
                className="eh-btn-secondary px-6 py-2"
                onClick={() => {
                  setStep(1);
                  if (slug) {
                    saveRegistrationProgress(slug, 1, buildFormSnapshot()).catch(
                      () => {},
                    );
                  }
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="eh-btn-primary px-6 py-2"
                onClick={handleGoToReview}
              >
                Review &amp; pay
              </button>
              <button
                type="button"
                className="eh-btn-secondary px-6 py-2"
                onClick={handleStartOver}
              >
                Start over
              </button>
            </div>
          </div>
        )}

        {step === 3 && priceSummary && (
          <div className="space-y-4">
            <h2 className="font-semibold text-lg">Confirm and pay</h2>
            <div className="border border-slate-200 p-4 text-sm space-y-1">
              <p>
                <span className="font-medium">Name:</span>{" "}
                {signedInFirstName.trim()} {signedInLastName.trim()}
              </p>
              <p>
                <span className="font-medium">Email:</span> {signedInEmail}
              </p>
              <p>
                <span className="font-medium">Country:</span> {country}
                {state ? `, ${state}` : ""}
              </p>
              <p>
                <span className="font-medium">Registration:</span>{" "}
                {primaryCategory === "full_conference"
                  ? isPaperPlusAddon
                    ? "Full Registration"
                    : "Full Conference"
                  : primaryCategory === "single_day"
                    ? "Listener"
                    : catalog.testTicket?.name ?? "Test ticket"}
              </p>
              <p>
                <span className="font-medium">Attendance:</span>{" "}
                {participationFormat === "virtual" ? "Virtual" : "Onsite / in-person"}
              </p>
              {priceSummary.extraPageCount > 0 && (
                <p>
                  Extra pages ({priceSummary.extraPageCount}): {symbol}
                  {(priceSummary.extraPagePence / 100).toFixed(2)}
                </p>
              )}
              {priceSummary.additionalPaperCount > 0 && (
                <p>
                  Additional papers ({priceSummary.additionalPaperCount}):{" "}
                  {symbol}
                  {(priceSummary.additionalPaperPence / 100).toFixed(2)}
                </p>
              )}
              {(priceSummary.addOnCount ?? 0) > 0 && (
                <p>
                  <span className="font-medium">Additional people:</span>{" "}
                  {papers
                    .flatMap((paper) => paper.addOnRegistrants ?? [])
                    .map((p) => {
                      const mid = p.memberId?.trim();
                      const base = `${p.name.trim()} <${p.email.trim()}>`;
                      return mid ? `${base} (IEEE ${mid})` : base;
                    })
                    .join("; ")}{" "}
                  ({symbol}
                  {((priceSummary.addOnPence ?? 0) / 100).toFixed(2)})
                </p>
              )}
              {banquetQuantity > 0 && (
                <p>
                  <span className="font-medium">Banquet tickets:</span>{" "}
                  {banquetQuantity}
                </p>
              )}
              {primaryCategory === "full_conference" &&
                papers.some((paper) => paper.paperId.trim()) && (
                  <p>
                    <span className="font-medium">Paper ID:</span>{" "}
                    {papers
                      .map((paper) => paper.paperId.trim())
                      .filter(Boolean)
                      .join("; ")}
                  </p>
                )}
              {primaryCategory === "full_conference" &&
                papers.some((paper) => paper.paperTitle.trim()) && (
                  <p>
                    <span className="font-medium">Paper title:</span>{" "}
                    {papers
                      .map((paper) => paper.paperTitle.trim())
                      .filter(Boolean)
                      .join("; ")}
                  </p>
                )}
              {!isPaperPlusAddon &&
                primaryCategory === "full_conference" &&
                papers.some((paper) => paper.paperCount.trim()) && (
                  <p>
                    <span className="font-medium">Paper pages:</span>{" "}
                    {papers
                      .map((paper) => paper.paperCount.trim())
                      .filter(Boolean)
                      .join("; ")}
                    {priceSummary.extraPageCount > 0 && catalog.extraPageFee && (
                      <>
                        {" "}
                        ({priceSummary.extraPageCount} extra @ {symbol}
                        {(catalog.extraPageFee.pricePerPagePence / 100).toFixed(0)}
                        /page)
                      </>
                    )}
                  </p>
                )}
              {estimatedDiscountPence > 0 && (
                <p>
                  <span className="font-medium">Discount ({appliedDiscountCode}):</span>{" "}
                  -{symbol}
                  {(estimatedDiscountPence / 100).toFixed(2)}
                </p>
              )}
              <p className="font-bold pt-2">
                Total: {symbol}
                {((priceSummary.total - estimatedDiscountPence) / 100).toFixed(2)}{" "}
                {currency}
              </p>
            </div>
            <p className="text-sm text-slate-500">
              You will be redirected to Stripe to pay in USD by card or Alipay.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                type="button"
                className="eh-btn-secondary px-6 py-2"
                onClick={() => {
                  setStep(2);
                  if (slug) {
                    saveRegistrationProgress(slug, 2, buildFormSnapshot()).catch(
                      () => {},
                    );
                  }
                }}
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="button"
                className="eh-btn-primary px-6 py-2 disabled:opacity-60"
                onClick={handlePay}
                disabled={isSubmitting}
              >
                Pay with Card or Alipay
              </button>
              <button
                type="button"
                className="eh-btn-secondary px-6 py-2"
                onClick={handleStartOver}
                disabled={isSubmitting}
              >
                Start over
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
